"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
const wp_query_params = {
  per_page: 3,
  order: "desc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
function FeaturedPropertiesConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="146952" className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="p-8 h-[400px] animate-pulse bg-muted/10" />)}
      </div>;
  }
  if (error || products.length === 0) {
    return <div className="p-8 text-center text-muted-foreground text-sm border-t border-border">
        No recent installations available to display at this time.
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-border">
      {products.slice(0, 3).map((product, i) => <article key={product.id} data-index={i} className="group flex flex-col bg-background hover:bg-accent/5 transition-all duration-500 hover:-translate-y-1">
          <Link to={product.link} className="block relative aspect-[4/3] overflow-hidden bg-muted border-b border-white/10">
            {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                Project Image
              </div>}
            <div className="absolute top-4 left-4">
              <Badge className="bg-primary text-primary-foreground rounded-md uppercase tracking-widest text-[10px] border-none px-3 py-1 shadow-lg shadow-primary/20">
                Featured Project
              </Badge>
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </Link>
          <div className="p-6 lg:p-8 flex flex-col flex-grow border-white/5">
            <h4 className="text-xl font-serif font-medium tracking-tight mb-3 text-foreground group-hover:text-primary transition-colors">
              {product.title}
            </h4>
            {product.short_description && <div className="text-sm text-muted-foreground line-clamp-2 mb-8 font-sans leading-relaxed" dangerouslySetInnerHTML={{
          __html: product.short_description
        }} />}
            <div className="mt-auto pt-6 border-t border-white/10">
              <AddToCartButton productId={product.id} variant="outline" className="w-full" buttonClassName="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-primary-foreground rounded-lg w-full uppercase tracking-widest text-xs h-12 border-none shadow-md hover:shadow-primary/30 transition-all" text="View Project Details" showQuantity={false} />
            </div>
          </div>
        </article>)}
    </div>;
}
export default function FirmOverview() {
  const expertiseAreas = ["uPVC Roofline Replacements", "Dry Verge & Dry Ridge Systems", "EPDM Rubber Roofing Solutions", "Guttering & Fascia Installations", "North East England Coverage"];
  return <section id="photogallery" className="bg-background text-foreground flex flex-col border-y border-border">
      
      <div className="grid grid-cols-1 md:grid-cols-12 lg:grid-cols-12 gap-6 md:gap-8 border-b border-border">
        <div className="md:col-span-5 lg:col-span-5 p-8 md:p-12 lg:p-16 xl:p-24 border-b lg:border-b-0 lg:border-r border-border flex flex-col justify-center bg-card">
          <Badge variant="outline" className="mb-8 self-start rounded-md border-primary/30 tracking-widest uppercase text-[10px] text-primary bg-primary/5">
            Five Star Excellence
          </Badge>
          <h2 className="text-4xl lg:text-5xl xl:text-6xl font-serif font-light tracking-tighter mb-8 leading-[1.1] text-foreground">
            Superior Roofing Solutions in the North East
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed mb-10 font-sans">Five Five Star Roofline is proud to deliver some of the finest installations the North East has to offer. Every project is completed to the highest standard and comes fully backed by a reliable warranty you can trust. With a commitment to quality and customer satisfaction, Five Star Roofline ensures your home is protected and looks its best for years to come.</p>

          <div className="space-y-4 mb-12">
            {expertiseAreas.map((item, i) => <div key={i} data-index={i} className="flex items-center gap-4 text-sm tracking-wide group">
                <div className="w-2 h-2 bg-primary rounded-full shadow-[0_0_8px_rgba(234,179,8,0.5)]" />
                <span className="group-hover:text-primary transition-colors">{item}</span>
              </div>)}
          </div>

          <Button asChild variant="outline" size="lg" className="self-start rounded-lg border-primary/50 hover:bg-primary hover:text-primary-foreground transition-all uppercase tracking-widest text-xs h-12 px-8 shadow-sm hover:shadow-primary/20">
            <Link to="/ServicesPage">
              Explore Our Services <ArrowRight className="ml-3 size-4" />
            </Link>
          </Button>
        </div>

        <div className="md:col-span-7 lg:col-span-7 relative min-h-[500px] lg:min-h-full bg-muted overflow-hidden">
          <img src="https://fivestarsroofline.com/wp-content/uploads/2026/03/626529845_1495323095927454_6784815751320281840_n-1-2.jpg" alt="Five Star Roofline Project Showcase" className="absolute inset-0 w-full h-full object-cover transition-all duration-1000 scale-105 hover:scale-100" />
          <div className="absolute inset-0 pointer-events-none border-[20px] border-primary/10 mix-blend-overlay" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/40 to-transparent" />
        </div>
      </div>

      
      <div className="flex flex-col">
        <div className="p-8 md:px-12 lg:px-16 py-10 border-b border-border flex flex-col items-center md:flex-row md:justify-between md:items-end gap-6 bg-accent/5 backdrop-blur-sm">
          <div>
            <h3 className="text-3xl font-serif font-light tracking-tight text-foreground">
              Recent Installations
            </h3>
            <p className="text-sm text-muted-foreground mt-2 font-sans max-w-md">
              A showcase of our high-quality uPVC and roofing projects completed for homeowners across the North East region.
            </p>
          </div>
          <Link to="/OurWorkPage" className="text-xs font-semibold uppercase tracking-widest text-primary hover:text-primary/80 transition-all flex items-center gap-2 group">
            View Full Gallery <ArrowRight className="size-3 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <EcommerceProductsProvider className="products-grid-wrapper" wp_query={{
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <FeaturedPropertiesConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}